import { CombinationKind, ExerciseTypesEnum, ExecutionModel, WorkoutId, ExecutionModelv2 } from "./trainingmodel";


export enum CompletionEnum {
  exceeded = 'exceeded',
  complete = 'complete',
  twothirds = 'twothirds',
  onethird = 'onethird',
  zero = 'zero',
  NA = 'NA'
}

export type CompletionType = {
  weight?: number;
  completion: CompletionEnum;
  note: string;
}

export interface IExerciseLogEntry {
  exercise: string;
  execution: ExecutionModel;
  type: ExerciseTypesEnum;
  completion: CompletionType[];
  notes?: string;
}

export class ExerciseLogEntry implements IExerciseLogEntry {
  exercise: string;
  execution: ExecutionModel;
  type: ExerciseTypesEnum;
  completion: CompletionType[];
  notes?: string;

  constructor(data: Partial<IExerciseLogEntry>) {
    this.exercise = data.exercise ?? '';
    this.execution = data.execution ?? { series: [] };
    this.type = data.type ?? ExerciseTypesEnum.weight;
    this.completion = data.completion ?? [];
    this.notes = data.notes;
  }

}

export interface ITrainingDayLogEntry {
  version: number;
  plansUUID: WorkoutId;
  workoutName: string;
  date: Date;
  log: IExerciseLogEntry[];
}

// TODO still consider if this is going to be working well to interface with a client feed

export class TrainingDayLog implements ITrainingDayLogEntry {
  version: number; // 1
  plansUUID: WorkoutId;
  workoutName: string;
  date: Date;
  log: ExerciseLogEntry[];

  constructor(data: ITrainingDayLogEntry) {
    this.version = data.version;
    this.plansUUID = data.plansUUID;
    this.workoutName = data.workoutName;
    this.date = data.date;
    this.log = data.log.map((e) => new ExerciseLogEntry(e));
  }
}


export interface IExerciseLogEntryv2 {
  exercise: string;
  execution: ExecutionModelv2;
  type: ExerciseTypesEnum;
  completion: CompletionType[];
  notes?: string;
}

export class ExerciseLogEntryv2 implements IExerciseLogEntryv2 {
  exercise: string;
  execution: ExecutionModelv2;
  type: ExerciseTypesEnum;
  completion: CompletionType[];
  notes?: string;

  constructor(data: Partial<IExerciseLogEntryv2>) {
    this.exercise = data.exercise ?? '';
    this.execution = data.execution ?? { series: [] };
    this.type = data.type ?? ExerciseTypesEnum.weight;
    this.completion = data.completion ?? [];
    this.notes = data.notes;
  }

}

export interface ITrainingDayLogEntryv2 {
  version: number;
  plansUUID: WorkoutId;
  workoutName: string;
  date: Date;
  log: IExerciseLogEntryv2[];
}

export class TrainingDayLogv2 implements ITrainingDayLogEntryv2 {
  version: number; // 2
  plansUUID: WorkoutId;
  workoutName: string;
  date: Date;
  log: ExerciseLogEntryv2[];

  constructor(data: ITrainingDayLogEntryv2) {
    this.version = data.version;
    this.plansUUID = data.plansUUID;
    this.workoutName = data.workoutName;
    this.date = data.date;
    this.log = data.log.map((e) => new ExerciseLogEntryv2(e));
  }
}

// ============================================================================
// Version 3 — block log (design of record:
// new-features/exercise_combination_data_design_v3_1.md §2).
// v3 log = v2 entries re-grouped, one BlockLogEntry per plan block, positions =
// FLATTENED block order (phases in stored order, blocks in phase order — see
// flattenDayBlocks in trainingmodel.ts). The block is the logging unit; solo =
// slots.length 1, which keeps carry-forward on today's exercises pixel-equal.
// INERT until the version guard + SW fleet turn (design §4.4).
// ============================================================================

export interface IBlockLogEntry {
  kind: CombinationKind;
  slots: IExerciseLogEntryv2[]; // v2 entry verbatim per slot; solo = slots.length 1
  notes?: string;               // block-level note (was per-exercise for solo)
}

export class BlockLogEntry implements IBlockLogEntry {
  kind: CombinationKind;
  slots: ExerciseLogEntryv2[];
  notes?: string;

  constructor(data: Partial<IBlockLogEntry> & { kind: CombinationKind }) {
    this.kind = data.kind;
    this.slots = (data.slots ?? []).map((e) => new ExerciseLogEntryv2(e));
    this.notes = data.notes;
  }
}

export interface ITrainingDayLogv3 {
  version: 3;
  plansUUID: WorkoutId;
  workoutName: string;
  date: Date;
  log: IBlockLogEntry[];
}

export class TrainingDayLogv3 implements ITrainingDayLogv3 {
  version: 3;
  plansUUID: WorkoutId;
  workoutName: string;
  date: Date;
  log: BlockLogEntry[];

  constructor(data: ITrainingDayLogv3) {
    this.version = data.version;
    this.plansUUID = data.plansUUID;
    this.workoutName = data.workoutName;
    this.date = data.date;
    this.log = data.log.map((e) => new BlockLogEntry({ kind: e.kind, slots: e.slots, notes: e.notes }));
  }
}
